import { useEffect, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronUp, UserCheck, Target } from 'lucide-react';
import { AnimatedAIChat } from '@/components/ui/animated-ai-chat';
import { supabase } from '@/lib/supabase';

import type { ChatLayoutContext } from './ChatMain';

const STAGES = ['pre-seed', 'seed', 'series-a', 'series-b', 'series-c', 'growth'];
const getToken = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token ?? '';
};

interface FundraiseProfile {
  company_name?: string;
  one_liner?: string;
  industry?: string;
  stage?: string;
  raise_amount?: number;
  raise_target_close?: string;
  company_location?: string;
}

function profileSummary(p: FundraiseProfile): string {
  return [p.company_name, p.stage, p.raise_amount ? `$${Number(p.raise_amount).toLocaleString()}` : null, p.industry]
    .filter(Boolean).join(' · ');
}

async function saveRaiseProfile(form: FundraiseProfile): Promise<FundraiseProfile | null> {
  try {
    const res = await fetch('/api/backr/profile/fundraise', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${await getToken()}` },
      body: JSON.stringify(form),
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data.profile ?? null;
  } catch { return null; }
}

export default function NewChat() {
  const navigate = useNavigate();
  const { createChat } = useOutletContext<ChatLayoutContext>();

  const [profile, setProfile] = useState<FundraiseProfile | null>(null);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  const [form, setForm] = useState<FundraiseProfile>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getToken()
      .then((token) =>
        fetch('/api/backr/profile/fundraise', {
          headers: { Authorization: `Bearer ${token}` },
        })
      )
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        if (data?.profile) setProfile(data.profile);
        setProfileLoaded(true);
      })
      .catch(() => setProfileLoaded(true));
  }, []);

  const hasProfile = profile && (profile.company_name || profile.raise_amount || profile.stage);

  const handleSave = async () => {
    setSaving(true);
    const saved = await saveRaiseProfile(form);
    setSaving(false);
    if (saved) { setProfile(saved); setShowSetup(false); }
  };

  const handleDelete = async () => {
    try {
      await fetch('/api/backr/profile/fundraise', {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${await getToken()}` },
      });
      setProfile(null);
      setShowSetup(false);
    } catch { /* silent */ }
  };

  const f = (key: keyof FundraiseProfile) => ({
    value: (form[key] as string) ?? '',
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm(prev => ({ ...prev, [key]: e.target.value })),
  });

  const handleCreateChat = (value: string) => {
    if (!value) return;
    navigate(`/chat/${createChat(value)}`);
  };

  return (
    <motion.section
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      className="mx-auto flex h-full w-full max-w-[50rem] flex-col"
    >
      <div className="flex flex-1 flex-col items-center justify-center px-4 pb-10 pt-6 sm:px-6">

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.38, ease: [0.16, 1, 0.3, 1] }}
          className="w-full text-center"
        >
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-[#2B7FFF]/20 bg-[#2B7FFF]/[0.08] px-3.5 py-1.5 dark:border-[#2B7FFF]/15 dark:bg-[#2B7FFF]/[0.1]">
            <span className="h-[7px] w-[7px] rounded-full bg-[#2B7FFF] shadow-[0_0_7px_rgba(43,127,255,0.85)]" />
            <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#2B7FFF]">RaiseBeacon AI</span>
          </div>

          <h1 className="text-[2.1rem] font-semibold leading-[1.13] tracking-[-0.025em] text-slate-900 dark:text-white sm:text-[2.65rem]">
            What can I help you with today?
          </h1>
          <p className="mt-3 text-[13.5px] text-slate-500 dark:text-white/35">
            Search investors, craft outreach, or prepare for your next meeting.
          </p>

          {profileLoaded && (
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.28, delay: 0.15 }}
              className="mt-4 flex justify-center"
            >
              {hasProfile ? (
                <button
                  type="button"
                  onClick={() => { setForm(profile!); setShowSetup(true); }}
                  className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200/70 bg-emerald-50/80 px-3 py-1 transition-colors hover:bg-emerald-100/80 dark:border-emerald-500/20 dark:bg-emerald-500/[0.08] dark:hover:bg-emerald-500/[0.14]"
                >
                  <UserCheck className="h-3 w-3 text-emerald-500 dark:text-emerald-400" />
                  <span className="text-[11px] font-medium text-emerald-700 dark:text-emerald-400">{profileSummary(profile!)}</span>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => { setForm({}); setShowSetup(true); }}
                  className="inline-flex items-center gap-1.5 rounded-full border border-amber-200/70 bg-amber-50/80 px-3 py-1 transition-colors hover:bg-amber-100/80 dark:border-amber-500/20 dark:bg-amber-500/[0.08] dark:hover:bg-amber-500/[0.14]"
                >
                  <Target className="h-3 w-3 text-amber-500 dark:text-amber-400" />
                  <span className="text-[11px] font-medium text-amber-700 dark:text-amber-400">Set up your raise for targeted advice</span>
                </button>
              )}
            </motion.div>
          )}
        </motion.div>

        <AnimatePresence>
          {showSetup && (
            <motion.div
              initial={{ opacity: 0, y: -8, height: 0 }}
              animate={{ opacity: 1, y: 0, height: 'auto' }}
              exit={{ opacity: 0, y: -8, height: 0 }}
              transition={{ duration: 0.22, ease: 'easeOut' }}
              className="mt-4 w-full overflow-hidden"
            >
              <div className="rounded-xl border border-[#2B7FFF]/20 bg-white shadow-[0_4px_24px_rgba(43,127,255,0.07)] dark:border-[#2B7FFF]/15 dark:bg-[rgba(10,16,36,0.85)]">
                <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3 dark:border-white/[0.06]">
                  <div className="flex items-center gap-2">
                    <Target className="h-3.5 w-3.5 text-[#2B7FFF]" />
                    <span className="text-[12px] font-semibold text-slate-700 dark:text-white/80">Your Raise</span>
                  </div>
                  <button type="button" onClick={() => setShowSetup(false)} className="text-slate-400 hover:text-slate-600 dark:text-white/25 dark:hover:text-white/50">
                    <ChevronUp className="h-4 w-4" />
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-3 p-4 sm:grid-cols-3">
                  <div className="col-span-2 sm:col-span-3">
                    <label className="mb-1 block text-[10.5px] font-semibold uppercase tracking-[0.1em] text-slate-400 dark:text-white/25">Company name</label>
                    <input type="text" maxLength={120} placeholder="Acme Inc." className="input-field" {...f('company_name')} />
                  </div>
                  <div className="col-span-2 sm:col-span-3">
                    <label className="mb-1 block text-[10.5px] font-semibold uppercase tracking-[0.1em] text-slate-400 dark:text-white/25">What you do</label>
                    <input type="text" maxLength={300} placeholder="We help X do Y." className="input-field" {...f('one_liner')} />
                  </div>
                  <div>
                    <label className="mb-1 block text-[10.5px] font-semibold uppercase tracking-[0.1em] text-slate-400 dark:text-white/25">Stage</label>
                    <select className="input-field" {...f('stage')}>
                      <option value="">Select…</option>
                      {STAGES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-[10.5px] font-semibold uppercase tracking-[0.1em] text-slate-400 dark:text-white/25">Raise amount</label>
                    <div className="relative">
                      <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-[13px] text-slate-400 dark:text-white/30">$</span>
                      <input
                        type="number" min={0} placeholder="500000" className="input-field pl-7"
                        value={form.raise_amount ?? ''}
                        onChange={e => setForm(prev => ({ ...prev, raise_amount: e.target.value ? Number(e.target.value) : undefined }))}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="mb-1 block text-[10.5px] font-semibold uppercase tracking-[0.1em] text-slate-400 dark:text-white/25">Industry</label>
                    <input type="text" maxLength={80} placeholder="Fintech, SaaS…" className="input-field" {...f('industry')} />
                  </div>
                  <div>
                    <label className="mb-1 block text-[10.5px] font-semibold uppercase tracking-[0.1em] text-slate-400 dark:text-white/25">Location</label>
                    <input type="text" maxLength={120} placeholder="New York, NY" className="input-field" {...f('company_location')} />
                  </div>
                  <div>
                    <label className="mb-1 block text-[10.5px] font-semibold uppercase tracking-[0.1em] text-slate-400 dark:text-white/25">Target close</label>
                    <input type="text" maxLength={30} placeholder="Q3 2026" className="input-field" {...f('raise_target_close')} />
                  </div>
                </div>

                <div className="flex justify-end gap-2 border-t border-slate-100 px-4 py-3 dark:border-white/[0.06]">
                  {hasProfile && (
                    <button type="button" onClick={handleDelete} className="mr-auto rounded-lg px-3.5 py-1.5 text-[12px] font-medium text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300">
                      Delete raise
                    </button>
                  )}
                  <button type="button" onClick={() => setShowSetup(false)} className="rounded-lg px-3.5 py-1.5 text-[12px] font-medium text-slate-500 hover:text-slate-700 dark:text-white/35 dark:hover:text-white/60">
                    Cancel
                  </button>
                  <button
                    type="button" onClick={handleSave} disabled={saving}
                    className="rounded-lg bg-[#2B7FFF] px-4 py-1.5 text-[12px] font-semibold text-white shadow-[0_1px_8px_rgba(43,127,255,0.4)] hover:bg-[#1A6AE8] disabled:opacity-50"
                  >
                    {saving ? 'Saving…' : 'Save raise'}
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.38, delay: 0.07, ease: [0.16, 1, 0.3, 1] }}
          className="mt-4 w-full"
        >
          <AnimatedAIChat onSubmit={handleCreateChat} />
        </motion.div>

      </div>
    </motion.section>
  );
}
